import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import Button from '../../../../components/atoms/button/Button'
import Loader from '../../../../components/atoms/loader/Loader'
import Text from '../../../../components/atoms/text/Text'
import Screen from '../../../../components/molecules/screen/Screen'
import routes, { generateRoute } from '../../../../config/routes'
import { CoworkingQueryClient } from '../../../../modules/CoworkingQueryClient'
import { useConfirmPaymentMutation } from '../../../../modules/vouchers/hooks'
import { useBookAssistant } from '../../../../providers/bookAssistant'
import { TYPES } from '../../../../providers/bookAssistant/types'
import { VARIANTS } from '../../../../types/Components.types'
import { ReactComponent as PaymentCompleteIllustration } from './assets/payment_complete_illustration.svg'
import styles from './Ok.module.css'

const Ok = () => {
  const { bank, paymentId, book } = useParams() as { bank: string; paymentId: string; book: string }
  const navigate = useNavigate()
  const location = useLocation()
  const { setData } = useBookAssistant()
  const [isWorking, setIsWorking] = useState(true)
  const queryParams = useRef(new URLSearchParams(location.search)).current
  const { mutate: confirmPayment } = useConfirmPaymentMutation()

  const handleContinueClick = useCallback(() => {
    const params = JSON.parse(atob(book))

    switch (params.type) {
      case TYPES.MONTH:
      case TYPES.DAY:
        navigate(routes.book.new.seat.confirmation, { replace: true, state: { goBack: routes.app } })
        return

      default:
        navigate(generateRoute(routes.book.new.meetingsRoom.details, [{ name: 'bookId', value: params.bookId }]), {
          replace: true,
        })
    }
  }, [navigate, book])

  const hasConfirmedPayment = useRef(false)

  useEffect(() => {
    if (hasConfirmedPayment.current) return
    hasConfirmedPayment.current = true

    confirmPayment(
      {
        bank,
        paymentId,
        merchantParameters: queryParams.get('Ds_MerchantParameters')!,
        signature: queryParams.get('Ds_Signature')!,
      },
      {
        onSuccess: () => {
          const params = JSON.parse(atob(book))
          params.date = new Date(params.date)
          setData({ data: params })
          setIsWorking(false)
          CoworkingQueryClient.invalidateQueries({ queryKey: ['reservated-seats'] })
        },
        onError: () => {
          setIsWorking(false)
        },
      },
    )
  }, [bank, paymentId, queryParams, book, confirmPayment, setData])

  return (
    <Screen hasDecorator decoratorSize="large">
      <div className={styles.container}>
        <div className={styles.content}>
          <PaymentCompleteIllustration />
          {!isWorking && (
            <>
              <Text size="l" align="center" variant={VARIANTS.primary} weight="700" className={styles.title}>
                ¡Listo!
              </Text>
              <Text className={styles.description} align="center">
                El pago de tu bono se ha realizado correctamente. ¡Ya puedes reservar tu sitio!
              </Text>
            </>
          )}
          {isWorking && <Loader className={styles.loader} variant="primary" />}
        </div>

        {!isWorking && (
          <Button variant={VARIANTS.primary} className={styles.action} onClick={handleContinueClick}>
            ¡Vamos!
          </Button>
        )}
      </div>
    </Screen>
  )
}

export default React.memo(Ok)
